<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import {
  REPLY_EDITOR_MODES,
  CHAR_LENGTH_WARNING,
  TRANSLATE_LANGUAGES,
} from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    // Feature Traducción automática: tercer segmento + selector de idioma.
    showTranslate: {
      type: Boolean,
      default: false,
    },
    translateLang: {
      type: String,
      default: 'en',
    },
  },
  emits: [
    'setReplyMode',
    'toggleEditorSize',
    'executeCopilotAction',
    'setTranslateLang',
  ],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleSetMode = mode => {
      if (props.isReplyRestricted && mode !== REPLY_EDITOR_MODES.NOTE) return;
      setReplyMode(mode);
    };

    const showTranslateLangMenu = ref(false);
    const toggleTranslateLangMenu = () => {
      showTranslateLangMenu.value = !showTranslateLangMenu.value;
    };
    const selectTranslateLang = lang => {
      emit('setTranslateLang', lang);
      showTranslateLangMenu.value = false;
      // eslint-disable-next-line no-console
      console.log('[auto-translation] idioma destino seleccionado:', lang);
    };
    const closeTranslateLangMenu = () => {
      showTranslateLangMenu.value = false;
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleSetMode,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      TRANSLATE_LANGUAGES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
      showTranslateLangMenu,
      toggleTranslateLangMenu,
      selectTranslateLang,
      closeTranslateLangMenu,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      :show-translate="showTranslate"
      @set-mode="handleSetMode"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <div
        v-if="showTranslate && mode === REPLY_EDITOR_MODES.TRANSLATE"
        v-on-click-outside="closeTranslateLangMenu"
        class="relative flex items-center"
      >
        <button
          type="button"
          class="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm text-n-blue-11 hover:bg-n-alpha-2 transition-all duration-100 ease-out"
          :title="$t('CONVERSATION.REPLYBOX.TRANSLATE_LANG_TOOLTIP')"
          @click="toggleTranslateLangMenu"
        >
          <span class="i-lucide-languages size-4" />
          <span class="font-medium uppercase">{{ translateLang }}</span>
          <span class="i-lucide-chevron-up size-3.5" />
        </button>
        <div
          v-if="showTranslateLangMenu"
          class="absolute bottom-full mb-2 ltr:right-0 rtl:left-0 z-40 flex flex-col gap-0.5 min-w-40 max-h-72 overflow-y-auto bg-n-alpha-3 backdrop-blur-[100px] p-1 shadow-lg rounded-lg border border-n-weak dark:border-n-strong/50"
        >
          <button
            v-for="lang in TRANSLATE_LANGUAGES"
            :key="lang.value"
            type="button"
            class="flex items-center justify-between gap-2 px-2.5 h-8 rounded-md text-sm text-n-slate-12 hover:bg-n-alpha-2 text-left"
            :class="{ '!bg-n-alpha-2': lang.value === translateLang }"
            @click="selectTranslateLang(lang.value)"
          >
            <span>{{ lang.label }}</span>
            <span
              v-if="lang.value === translateLang"
              class="i-lucide-check size-4"
            />
          </button>
        </div>
      </div>
      <template v-if="captainTasksEnabled">
        <div class="relative">
          <NextButton
            ref="copilotToggleRef"
            ghost
            :disabled="disabled || isEditorDisabled"
            :class="{
              'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
              'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
            }"
            sm
            icon="i-ph-sparkle-fill"
            @click="toggleCopilotMenu"
          />
          <CopilotMenuBar
            v-if="showCopilotMenu"
            v-on-click-outside="[
              handleClickOutside,
              { ignore: [copilotToggleRef] },
            ]"
            :has-selection="false"
            :has-content="hasContent"
            :conversation-id="conversationId"
            class="ltr:right-0 rtl:left-0 bottom-full mb-2"
            @execute-copilot-action="handleCopilotAction"
          />
        </div>
        <NextButton
          ghost
          class="text-n-slate-11"
          sm
          icon="i-lucide-maximize-2"
          @click="$emit('toggleEditorSize')"
        />
      </template>
    </div>
  </div>
</template>
